import {MotionProps} from 'framer-motion'
import {ComboboxRootProps} from '../../components/Combobox/types'
import {Appearance} from '../../components/AtlasLozenge/types'

import {SxProp} from '../..'

// ------------------------------
/**
 * Common types & utility types
 */
export type TestIdType = {
  /** A `testId` prop is provided for specified elements, which is a unique string that appears as a data attribute `data-testid` in the rendered code, serving as a hook for automated tests */
  testId?: string
}
export type RuleBuilderCondition = 'if' | 'when' | 'then'
export type Spacing = 'compact' | 'default' | 'relaxed' | 'none'

const SIDE_OPTIONS = ['top', 'right', 'bottom', 'left'] as const
const ALIGN_OPTIONS = ['start', 'center', 'end'] as const
// @TODO: change this type to `'small' | 'md' | 'lg'`
const SIZE_OPTIONS = ['sm', 'md', 'lg'] as const
const DIRECTION_OPTIONS = ['down', 'right', 'left' as const]
const VALIDATION_STATUSES = ['success', 'error', 'warning', 'default']

export type Size = typeof SIZE_OPTIONS[number]
export type Side = typeof SIDE_OPTIONS[number]
export type Direction = typeof DIRECTION_OPTIONS[number]
export type TransitionStatus = Appearance
export type ValidationStatus = typeof VALIDATION_STATUSES[number]

// @TODO
// until BoxWithIndicator supports top and bottom placement, we'll just be using 'left' and 'right'
export type ArrowPlacement = Omit<Side, 'top' | 'bottom'>

type StatusTypes = {
  [key: string]: string
}
export const STATUSES: StatusTypes = {
  inprogress: 'in progress',
  default: 'default',
  moved: 'moved',
  new: 'new',
  removed: 'removed',
  success: 'success',
  done: 'done',
  todo: 'todo',
  any: 'any',
  locked: 'locked'
}
/**
 * Component props
 */

// ------------------------------
/** A combobox for rendering a filtered list of items a user can select from */
export interface ActionsFilterProps<T extends object> extends ComboboxRootProps<T> {
  /**
   *
   */
  label?: string
  /**
   * The list of items to pass in to the Actions Filter.
   */
  items: Iterable<T>
  /**
   * The filter function used to determine if a option should be included in the Actions Filter list.
   */
  defaultFilter?: (textValue: string, inputValue: string) => boolean
  /**
   *
   */
  children: React.ReactNode
  /**
   * Additional helper text to associate with the Actions Filter
   */
  description?: string
  /**
   * Message to help a user fix a validation error.
   */
  errorMessage?: string
  /**
   * Whether to display the count of option types in the Actions Filter list
   */
  displayCount?: boolean
}
// ------------------------------
/** Renders a box with a caret indicator positioned vertically. Maintains awareness of the y-axis center of a given element in the corresponding pane of the rule builder layout */
type BoxWithIndicatorType = React.PropsWithChildren<{
  /**
   * Size of the arrow indicator
   * @default 'lg'
   */
  arrowSize?: Size
  /**
   * Distance from the top element that the caret indicator should render
   * @default 44
   */
  caretTop?: number
  /**
   * Whether the arrow should be animated or not
   */
  isArrowAnimated?: boolean
  /**
   * Side that the caret indicator should render
   */
  side?: ArrowPlacement
  /**
   * Whether to hide the arrow
   * @default false
   */
  hideArrow?: boolean
  /**
   * Only to be used as a last resort
   */
  UNSAFE_className?: string
}>

interface BoxWithIndicatorArrowType extends MotionProps {
  arrowSize: Size
  caretTop: number
  side: ArrowPlacement
  /**
   * Only to be used as a last resort
   */
  UNSAFE_className?: string
}
// ------------------------------
/** A component that wraps children and calculates the vertical center position of its boundingClientRect using the useElementCenterY hook. */
type PositionAwareItemType = React.PropsWithChildren<{
  /**
   * A unique id
   */
  id?: string
  /**
   * An index when used in lists of items
   */
  index?: number
  /**
   * The children to render inside the position aware item
   */
  children: React.ReactNode
  /**
   * Whether to track the center-y value
   */
  trackCenterY?: boolean
  /**
   * Handler called when the top child in a list of position aware items' center-y value changes
   */
  onTopChildCenterYChange?: (centerY: number) => void
  /**
   * Whether the position aware item is selected
   */
  isSelected?: boolean
  /**
   * Handler to call when the position aware item is selected
   */
  onItemSelect?: () => void
  /**
   * Only to be used as a last resort
   */
  UNSAFE_className?: string
}>
// ------------------------------
type RuleBuilderStepsType = React.PropsWithChildren<{
  /**
   * The index of the currently active step
   */
  activeStep?: number
  /**
   * The Steps element should have a descriptive `aria-label` attribute to provide a descriptive label for the list of steps.
   * See: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-label
   */
  ariaLabel?: string
  /**
   * The initial selected Step
   */
  initialStep?: number
  /**
   * Handler called when a user selects on a step
   */
  onSelectStep?: (step: number) => void
  /**
   * State of the steps
   */
  state?: 'loading' | 'error'
  /**
   * The number in the sequence the step takes place
   */
  stepCount?: number
}>
// ------------------------------
/** Renders a containing element that describes the details contained with a rule, and consists of a container and a caret that follows a rule as it progresses through a workflow */
type RuleDetailType = React.PropsWithChildren<{
  /**
   * Side that the caret should render
   */
  side?: Side
  /**
   * Whether the Rule detail section should display a caret
   */
  hideArrow?: boolean
  /**
   * Vertical position of the indicator caret
   */
  caretTop?: number
  /**
   * Header height in pixels. If you use the RuleBuilderLayout.Header, this is necessary to handle the offset required for the caret indicator arrow to correctly position itself.
   * @default 0
   */
  headerHeight?: number
}>
// ------------------------------
// @TODO: RuleFilterType
// ------------------------------
/** Provides a visual indicator that a user has added a script or filter to an item. Use with a RuleFilter. */
type RuleFilterTagType = React.PropsWithChildren<{
  /**
   * Validation status described by the Filter Tag
   */
  validationStatus?: ValidationStatus
  /**
   * A visual that renders inside the Filter Tag before the description
   */
  leadingVisual?: JSX.Element | string | React.ComponentType<React.PropsWithChildren<{className?: string}>>
  /**
   * A visual that renders inside the Filter Tag after the description
   */
  trailingVisual?: JSX.Element | string | React.ComponentType<React.PropsWithChildren<{className?: string}>>
  /**
   * Determines whether to apply the bold style or not.
   */
  isBold?: boolean
}>
// ------------------------------
/** * A rule item used to describe an item in a workflow and provides actions a user can take. */
type RuleItemType = React.PropsWithChildren<{
  /**
   * Unique identifier for the RuleItem
   */
  id?: string
  /**
   * Set the amount of padding in the Rule Item
   */
  spacing?: Spacing
  /**
   * Whether to show a loading indicator in the Rule Item
   */
  loading?: boolean
  /**
   * Which position to render the loading indicator
   * 'auto' (default): at the end of the rule item, unless a `leadingVisual` is passed. Then, it will render at the beginning
   * 'leading': at the beginning of the rule item
   * 'trailing': at the end of the rule item
   */
  loaderPosition?: 'auto' | 'leading' | 'trailing'
  /**
   * A visual that renders inside the Rule Item before the title and description
   */
  leadingVisual?: React.ReactNode | string | React.ComponentType<React.PropsWithChildren<{className?: string}>>
  /**
   * A visual that renders inside the Rule Item after the title and description
   */
  trailingVisual?: React.ReactNode | string | React.ComponentType<React.PropsWithChildren<{className?: string}>>
  /**
   * A visual indicator that renders inside the Rule Item after trailing Visual
   */
  trailingAction?: React.ReactElement<React.HTMLProps<HTMLButtonElement>>
  /**
   * A number corresponding to the count of elements described in the Rule Item.
   */
  counter?: number | string
  /**
   * Change the Counter Badge and Rule Item border style to indicate the Rule Item is selected
   */
  isSelected?: boolean
  /**
   * Whether the rule item is disabled
   */
  disabled?: boolean
  /**
   * Event handler called when the user selects a rule item (via mouse or keyboard).
   */
  onSelect?: (event: React.MouseEvent) => void
  /**
   * Only to be used as a last resort
   */
  UNSAFE_className?: string
}>
// ------------------------------
/** Step Connector */
type RuleStepConnectorType = React.PropsWithChildren<{
  /** */
  isLastStep?: boolean
  /** */
  isSelectedStep?: boolean
  /** */
  isCompletedStep?: boolean
  /** */
  direction?: Direction
  /** */
  index?: number
}>
// ------------------------------
/** Displays a Rule Builder Step Label, which describes a condition. */
type RuleStepLabelType = React.PropsWithChildren<{
  /**
   * Condition described by the step label
   */
  condition?: RuleBuilderCondition
  /**
   * Whether the step is completed
   */
  isCompletedStep?: boolean
  /**
   * Whether the label is for the current step
   */
  isCurrentStep?: boolean
  /**
   * Whether the Step label is selected
   */
  isSelected?: boolean
  /**
   * Opacity of the Step
   */
  opacity?: number
  /**
   * Label
   */
  label: string | React.ReactNode
  /**
   * Description
   */
  description?: string
  /**
   * Only to be used as a last resort
   */
  UNSAFE_className?: string
}>
// ------------------------------
/** Describes a transition status */
type RuleTransitionType = {
  /**
   * Starting status from which a task or entity is to be transitioned.
   */
  fromStatus?: TransitionStatus
  /**
   * Target status to which a task or entity is to be transitioned.
   */
  targetStatus?: TransitionStatus
}
// -------------------------------
/** Scrollable list, with items in an overflow container */
type ScrollableListType = React.PropsWithChildren<{
  /**
   * Optional height to provide the Scrollable List
   * @default 408
   */
  height?: number
  /**
   * Spacing for list Scrollable List viewport container
   * @default 'none'
   */
  spacing?: Spacing
}>
// @TODO: ScrollableListItemType
// -------------------------------
// Exports
export type BoxWithIndicatorProps = BoxWithIndicatorType & TestIdType & SxProp
export type BoxWithIndicatorArrowProps = BoxWithIndicatorArrowType & TestIdType & SxProp
export type PositionAwareItemProps = PositionAwareItemType & TestIdType & SxProp
export type RuleBuilderStepsProps = RuleBuilderStepsType & TestIdType & SxProp
export type RuleDetailProps = RuleDetailType & TestIdType & SxProp
export type RuleFilterTagProps = RuleFilterTagType & TestIdType & SxProp
export type RuleItemProps = RuleItemType & TestIdType & SxProp
export type RuleStepConnectorProps = RuleStepConnectorType & TestIdType & SxProp
export type RuleStepLabelProps = RuleStepLabelType & TestIdType & SxProp
export type RuleTransitionProps = RuleTransitionType & TestIdType & SxProp
export type ScrollableListProps = ScrollableListType & TestIdType & SxProp
//
